import React from "react";
import styled from "styled-components";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";

import PreTitle from "../components/PreTitle";
import P from "../components/P";
import LeadCaptureForm from "../components/LeadCaptureForm";
import { buildWhatsappUrl } from "../utils/whatsapp";
import { PROJECT_CASES } from "../data/projects";

import LOGO from "../assets/media/logo_nav.png";

const SERVICE_AREAS = [
    "Software sob medida para operação",
    "Automação de processos manuais",
    "Landing pages para campanhas",
    "CRM e operação comercial",
    "Qualidade, suporte e evolução"
];

const TRUST_METRICS = [
    { value: "+3", label: "anos de mercado" },
    { value: "+20", label: "projetos entregues" },
    { value: "2", label: "países atendidos" }
];

const SITE_URL = "https://www.mangosoftwarehouse.com";
const CAPTURE_URL = `${SITE_URL}/captacao`;
const DEFAULT_PAGE_TITLE = "Mango | Software sob medida, automação e sites";
const DEFAULT_PAGE_DESCRIPTION = "Software sob medida, automação, sites e consultoria de TI para empresas que precisam organizar processos, vender melhor e crescer com mais segurança.";
const CAPTURE_PAGE_TITLE = "Diagnóstico para software, automação e landing pages | Mango";
const CAPTURE_PAGE_DESCRIPTION = "Solicite uma análise inicial da Mango para transformar processos, ideias e demandas comerciais em software sob medida, automações ou landing pages de conversão.";

function setMetaTag(selector, attributes) {
    if (typeof document === "undefined") {
        return;
    }

    let element = document.head.querySelector(selector);

    if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
    }

    Object.entries(attributes).forEach(([attribute, value]) => {
        element.setAttribute(attribute, value);
    });
}

function setLinkTag(selector, attributes) {
    if (typeof document === "undefined") {
        return;
    }

    let element = document.head.querySelector(selector);

    if (!element) {
        element = document.createElement("link");
        document.head.appendChild(element);
    }

    Object.entries(attributes).forEach(([attribute, value]) => {
        element.setAttribute(attribute, value);
    });
}

function setStructuredData(id, payload) {
    if (typeof document === "undefined") {
        return;
    }

    let element = document.getElementById(id);

    if (!element) {
        element = document.createElement("script");
        element.id = id;
        element.type = "application/ld+json";
        document.head.appendChild(element);
    }

    element.textContent = JSON.stringify(payload);
}

function removeStructuredData(id) {
    if (typeof document === "undefined") {
        return;
    }

    const element = document.getElementById(id);

    if (element) {
        element.remove();
    }
}

const Page = styled.main`
    min-height: 100svh;
    width: 100%;
    overflow-x: hidden;
    background:
        radial-gradient(circle at 12% 18%, rgba(139, 92, 246, 0.1), transparent 24%),
        radial-gradient(circle at 86% 8%, rgba(255, 90, 191, 0.08), transparent 20%),
        linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(250, 247, 255, 1));
`

const Shell = styled.div`
    width: min(1120px, calc(100vw - 32px));
    min-height: 100svh;
    margin: 0 auto;
    padding: 28px 0 42px;
    display: grid;
    grid-template-rows: auto 1fr;
    gap: 38px;

    @media only screen and (max-width: 640px) {
        width: min(100%, calc(100vw - 24px));
        padding: 18px 0 34px;
        gap: 28px;
    }
`

const Header = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;

    @media only screen and (max-width: 520px) {
        gap: 12px;
    }
`

const LogoLink = styled.a`
    display: inline-flex;
    align-items: center;
`

const Logo = styled.img`
    width: 104px;
    height: auto;

    @media only screen and (max-width: 520px) {
        width: 88px;
    }
`

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`

const HeaderLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 999px;
    color: var(--ink);
    background: rgba(255, 255, 255, 0.82);
    border: 1px solid rgba(126, 94, 221, 0.12);
    font-size: 0.94rem;
    font-weight: 700;
    box-shadow: var(--shadow-soft);

    @media only screen and (max-width: 520px) {
        display: none;
    }
`

const HomeLink = styled(HeaderLink)`
    @media only screen and (max-width: 520px) {
        display: inline-flex;
        min-height: 42px;
        padding: 0 14px;
        font-size: 0.9rem;
    }
`

const Hero = styled.section`
    display: grid;
    grid-template-columns: minmax(0, 0.82fr) minmax(360px, 0.74fr);
    gap: 44px;
    align-items: center;

    @media only screen and (max-width: 920px) {
        grid-template-columns: 1fr;
        align-items: flex-start;
        gap: 28px;
    }

    @media only screen and (max-width: 640px) {
        gap: 22px;
    }
`

const Copy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 22px;
`

const Title = styled.h1`
    margin: 0;
    max-width: 11ch;
    font-family: var(--font-display);
    font-size: clamp(3rem, 6.2vw, 5.45rem);
    line-height: 0.94;
    letter-spacing: -0.055em;
    color: var(--ink);
    text-wrap: balance;

    @media only screen and (max-width: 600px) {
        max-width: 13ch;
        font-size: 2.35rem;
        line-height: 1;
        letter-spacing: -0.025em;
    }
`

const LeadText = styled.p`
    margin: 0;
    max-width: 54ch;
    color: var(--muted);
    font-size: 1.08rem;
    font-weight: 500;
    line-height: 1.72;

    @media only screen and (max-width: 600px) {
        font-size: 1rem;
        line-height: 1.62;
    }
`

const Actions = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`

const PrimaryLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 54px;
    padding: 0 22px;
    border-radius: 999px;
    color: #ffffff;
    font-weight: 700;
    background: linear-gradient(135deg, var(--brand), var(--brand-2));
    border: 1px solid rgba(255, 255, 255, 0.82);
    box-shadow: 0 18px 32px rgba(117, 83, 255, 0.18);
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 24px 42px rgba(117, 83, 255, 0.28);
    }

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`

const SecondaryLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 54px;
    padding: 0 22px;
    border-radius: 999px;
    color: var(--ink);
    font-weight: 700;
    background: rgba(255, 255, 255, 0.84);
    border: 1px solid rgba(126, 94, 221, 0.12);
    box-shadow: var(--shadow-soft);
    transition: transform 0.25s ease, background-color 0.25s ease;

    &:hover {
        transform: translateY(-2px);
        background: rgba(255, 255, 255, 0.96);
    }

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`

const ProofList = styled.div`
    display: grid;
    gap: 10px;
    max-width: 560px;
`

const ProofItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: var(--ink);

    svg {
        flex: 0 0 auto;
        margin-top: 2px;
        color: #1f8f5b;
    }
`

const FormColumn = styled.div`
    display: grid;
    gap: 14px;
`

const FormNote = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 22px;
    background: rgba(233, 249, 244, 0.88);
    border: 1px solid rgba(58, 184, 119, 0.16);
    color: var(--ink);

    svg {
        flex: 0 0 auto;
        margin-top: 2px;
        color: #1f8f5b;
    }
`

const FinePrint = styled.p`
    margin: 0;
    max-width: 58ch;
    color: var(--muted);
    font-size: 0.92rem;
    font-weight: 500;
    line-height: 1.72;
`

const SupportGrid = styled.section`
    display: grid;
    gap: 28px;
`

const AreaPanel = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(360px, 0.8fr);
    gap: 24px;
    align-items: stretch;
    padding: 28px;
    border-radius: 30px;
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.88), rgba(244, 238, 255, 0.78)),
        rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(126, 94, 221, 0.12);
    box-shadow: var(--shadow-soft);
    backdrop-filter: blur(16px);

    @media only screen and (max-width: 860px) {
        grid-template-columns: 1fr;
        padding: 22px;
    }

    @media only screen and (max-width: 640px) {
        padding: 20px;
        border-radius: 20px;
    }
`

const AreaCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const SupportTitle = styled.h2`
    margin: 0;
    font-family: var(--font-display);
    font-size: clamp(1.45rem, 2.3vw, 1.9rem);
    line-height: 1.02;
    color: var(--ink);
`

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;

    @media only screen and (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`

const MetricCard = styled.div`
    display: grid;
    align-content: center;
    min-height: 112px;
    padding: 18px;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(126, 94, 221, 0.1);
`

const MetricValue = styled.strong`
    display: block;
    margin-bottom: 4px;
    font-family: var(--font-display);
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 0.96;
    color: var(--ink);
`

const MetricLabel = styled.span`
    color: var(--muted);
    font-size: 0.92rem;
    font-weight: 700;
`

const AreaList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-content: flex-start;
`

const AreaChip = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 36px;
    padding: 0 13px;
    border-radius: 999px;
    color: var(--ink);
    background: rgba(244, 238, 255, 0.84);
    border: 1px solid rgba(126, 94, 221, 0.1);
    font-size: 0.9rem;
    font-weight: 700;
`

const CasesBlock = styled.div`
    display: grid;
    gap: 18px;
    grid-column: 1 / -1;
    text-align: center;
`

const CasesTitle = styled.h2`
    margin: 0;
    font-family: var(--font-display);
    font-size: clamp(1.45rem, 2.3vw, 1.9rem);
    line-height: 1.02;
    color: var(--ink);
`

const CaseLogoStrip = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(26px, 6vw, 68px);
    min-height: 138px;
    padding: 22px 0;
    overflow-x: auto;
    border-top: 1px solid rgba(126, 94, 221, 0.12);
    border-bottom: 1px solid rgba(126, 94, 221, 0.12);

    @media only screen and (max-width: 720px) {
        justify-content: flex-start;
        min-height: 112px;
        padding: 16px 0;
    }
`

const CaseLogo = styled.img`
    width: auto;
    max-width: 220px;
    height: clamp(76px, 9vw, 104px);
    object-fit: contain;
    flex: 0 0 auto;
    filter: saturate(0.96);

    @media only screen and (max-width: 560px) {
        max-width: 170px;
        height: 72px;
    }
`

function LeadCapture () {
    React.useEffect(() => {
        document.title = CAPTURE_PAGE_TITLE;
        setMetaTag('meta[name="description"]', {
            name: "description",
            content: CAPTURE_PAGE_DESCRIPTION
        });
        setMetaTag('meta[name="robots"]', {
            name: "robots",
            content: "index, follow, max-image-preview:large"
        });
        setMetaTag('meta[property="og:title"]', {
            property: "og:title",
            content: CAPTURE_PAGE_TITLE
        });
        setMetaTag('meta[property="og:description"]', {
            property: "og:description",
            content: CAPTURE_PAGE_DESCRIPTION
        });
        setMetaTag('meta[property="og:url"]', {
            property: "og:url",
            content: CAPTURE_URL
        });
        setMetaTag('meta[property="og:image"]', {
            property: "og:image",
            content: `${SITE_URL}/logo.png`
        });
        setMetaTag('meta[name="twitter:title"]', {
            name: "twitter:title",
            content: CAPTURE_PAGE_TITLE
        });
        setMetaTag('meta[name="twitter:description"]', {
            name: "twitter:description",
            content: CAPTURE_PAGE_DESCRIPTION
        });
        setMetaTag('meta[name="twitter:image"]', {
            name: "twitter:image",
            content: `${SITE_URL}/logo.png`
        });
        setLinkTag('link[rel="canonical"]', {
            rel: "canonical",
            href: CAPTURE_URL
        });
        setStructuredData("lead-capture-service-schema", {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Mango",
            url: CAPTURE_URL,
            image: `${SITE_URL}/logo.png`,
            description: CAPTURE_PAGE_DESCRIPTION,
            areaServed: ["Brasil", "Portugal"],
            serviceType: [
                "Desenvolvimento de software sob medida",
                "Automação de processos",
                "Landing pages para campanhas",
                "CRM e operação comercial"
            ],
            brand: {
                "@type": "Brand",
                name: "Mango"
            }
        });

        return () => {
            document.title = DEFAULT_PAGE_TITLE;
            setMetaTag('meta[name="description"]', {
                name: "description",
                content: DEFAULT_PAGE_DESCRIPTION
            });
            setMetaTag('meta[property="og:title"]', {
                property: "og:title",
                content: DEFAULT_PAGE_TITLE
            });
            setMetaTag('meta[property="og:description"]', {
                property: "og:description",
                content: "A Mango cria sistemas, automações e páginas para empresas que precisam organizar processos e crescer com mais segurança."
            });
            setMetaTag('meta[property="og:url"]', {
                property: "og:url",
                content: SITE_URL
            });
            setMetaTag('meta[name="twitter:title"]', {
                name: "twitter:title",
                content: DEFAULT_PAGE_TITLE
            });
            setMetaTag('meta[name="twitter:description"]', {
                name: "twitter:description",
                content: "A Mango cria sistemas, automações e páginas para empresas que precisam organizar processos e crescer com mais segurança."
            });
            removeStructuredData("lead-capture-service-schema");
        };
    }, []);

    return (
        <Page>
            <Shell>
                <Header>
                    <LogoLink href="/" aria-label="Voltar para a página inicial da Mango">
                        <Logo src={LOGO} alt="Mango" />
                    </LogoLink>

                    <HeaderActions>
                        <HomeLink href="/">
                            Voltar ao site
                        </HomeLink>

                        <HeaderLink href={buildWhatsappUrl("Olá, vim pela página de captação da Mango e quero conversar sobre um projeto.")} target="_blank" rel="noreferrer">
                            Falar no WhatsApp
                        </HeaderLink>
                    </HeaderActions>
                </Header>

                <Hero id="top">
                    <Copy>
                        <PreTitle>Software sob medida, automação e landing pages</PreTitle>

                        <Title>Diagnóstico para seu projeto digital</Title>

                        <LeadText>
                            Descreva sua necessidade e receba uma análise inicial para transformar processo, ideia ou demanda comercial em uma solução digital viável.
                        </LeadText>

                        <Actions>
                            <PrimaryLink href="#formulario">
                                Receber diagnóstico
                                <ArrowOutwardRoundedIcon fontSize="small" />
                            </PrimaryLink>

                            <SecondaryLink href={buildWhatsappUrl("Olá, vim pela página de captação da Mango e quero conversar pelo WhatsApp.")} target="_blank" rel="noreferrer">
                                Falar com especialista
                                <ArrowOutwardRoundedIcon fontSize="small" />
                            </SecondaryLink>
                        </Actions>

                        <ProofList>
                            <ProofItem>
                                <TaskAltRoundedIcon fontSize="small" />
                                <P>Análise inicial com caminho, prioridade e próximo passo para o projeto.</P>
                            </ProofItem>
                            <ProofItem>
                                <TaskAltRoundedIcon fontSize="small" />
                                <P>Experiência em software, automação e páginas preparadas para conversão.</P>
                            </ProofItem>
                        </ProofList>
                    </Copy>

                    <FormColumn id="formulario">
                        <FormNote>
                            <TaskAltRoundedIcon />
                            <P>Preencha em menos de 2 minutos. A resposta inicial ajuda a organizar escopo e prioridade.</P>
                        </FormNote>

                        <LeadCaptureForm
                            title="Solicite uma análise inicial"
                            description="Conte o que precisa resolver para receber um direcionamento da Mango."
                            ariaLabel="Formulário de captação"
                        />

                        <FinePrint>
                            WhatsApp: +55 41 9115-0056
                        </FinePrint>
                    </FormColumn>
                </Hero>

                <SupportGrid aria-label="Atuação e cases da Mango">
                    <AreaPanel>
                        <AreaCopy>
                            <PreTitle>Atuação da Mango</PreTitle>
                            <SupportTitle>Projetos digitais para organizar operação, venda e presença digital.</SupportTitle>
                            <P>A Mango atua quando a empresa precisa sair de processos manuais, estruturar vendas ou validar uma solução digital com engenharia e interface no mesmo fluxo.</P>

                            <AreaList>
                                {SERVICE_AREAS.map((area) => (
                                    <AreaChip key={area}>{area}</AreaChip>
                                ))}
                            </AreaList>
                        </AreaCopy>

                        <MetricsGrid>
                            {TRUST_METRICS.map((metric) => (
                                <MetricCard key={metric.label}>
                                    <MetricValue>{metric.value}</MetricValue>
                                    <MetricLabel>{metric.label}</MetricLabel>
                                </MetricCard>
                            ))}
                        </MetricsGrid>
                    </AreaPanel>

                    <CasesBlock>
                        <PreTitle>Cases de sucesso</PreTitle>
                        <CasesTitle>Empresas que já transformaram processos e campanhas em soluções digitais com a Mango.</CasesTitle>

                        <CaseLogoStrip aria-label="Logos de cases de sucesso da Mango">
                            {PROJECT_CASES.map((project) => (
                                <CaseLogo key={project.name} src={project.logo} alt={project.logoAlt} title={project.name} />
                            ))}
                        </CaseLogoStrip>
                    </CasesBlock>
                </SupportGrid>
            </Shell>
        </Page>
    )
}

export { LeadCapture }
export default LeadCapture
